import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild } from '@angular/core';
import { Subject, Subscription, forkJoin } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';

import { AgentDashboardApiService } from 'src/app/services/agent-dashboard-api.service';
import {
  AgentDashboardSummary,
  AgentDashboardTrendPoint,
  AgentDashboardGranularity,
  AgentDashboardSla,
  AgentDashboardRecentItem,
  AGENT_DASHBOARD_IGNORE_REASONS,
} from 'src/app/shared/agent-dashboard';
import { GraphData } from 'src/app/shared/dashboard';

/** Formats a datetime as `yyyy-MM-dd` for the API's `from`/`to` query params. */
function toDateParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * API-13: Agent ops dashboard content -- embedded as the 5th accordion
 * section on `HomeComponent` (`portal-admin`-gated there via
 * `canSeeAgentDashboard`), not a standalone routed page. Date range +
 * granularity changes re-fetch the summary/trend/SLA panels together
 * (debounced `Subject`, same pattern `home-chart-annual-sales.component.ts`
 * already uses for its own date pickers); the recent-N list is independent
 * of the date range (always latest overall) and only re-fetches on its own
 * `limit` change.
 *
 * Data will be sparse until `AG-7`/`AG-9`/`AG-10`/`API-12` ship (see the
 * backend prompt's "Known gap") -- zeros in `served`/`ignored` and empty
 * customer/manager names are expected, not a loading failure.
 */
@Component({
  selector: 'app-agent-dashboard',
  templateUrl: './agent-dashboard.component.html',
  styleUrls: ['./agent-dashboard.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class AgentDashboardComponent implements OnInit, OnDestroy {

  dateFrom: Date;
  dateTo: Date;
  granularityOptions: { label: string; value: AgentDashboardGranularity }[] = [
    { label: 'День', value: 'day' },
    { label: 'Тиждень', value: 'week' },
    { label: 'Місяць', value: 'month' },
  ];
  selectedGranularity: AgentDashboardGranularity = 'day';

  summary: AgentDashboardSummary = null;
  trend: AgentDashboardTrendPoint[] = [];
  trendChartData: GraphData = null;
  sla: AgentDashboardSla = null;
  loaded: boolean = false;

  recentLimitOptions: number[] = [10, 20, 50, 100];
  recentLimit: number = 10;
  recent: AgentDashboardRecentItem[] = [];
  recentLoaded: boolean = false;

  @ViewChild('ptableRecent') ptableRecent: Table;
  /** Per-column filter row values, same `cfN`-per-table convention every other Home
   *  accordion table already uses (e.g. `home-recent-orders-list.component.ts`'s `cf1`). */
  cfRecent: any = {};

  /** Single-select: currently selected row in the recent table, null if none selected. */
  selectedRecent: AgentDashboardRecentItem | null = null;

  ignoreReasons = AGENT_DASHBOARD_IGNORE_REASONS;
  /** Single ignore-reason selection (applies to the currently selected row only). */
  selectedIgnoreReason: string | null = null;
  /** Keyed by id -- disables a row's action buttons while its own
   *  claim/ignore/spam call is in flight, so a slow response can't be
   *  double-fired by an impatient second click. */
  actionInProgress: Record<number, boolean> = {};

  /** True while the selected row's own claim/ignore/spam call is in flight. */
  get selectionBusy(): boolean {
    return !!this.selectedRecent && !!this.actionInProgress[this.selectedRecent.id];
  }

  /** Action-bar enablement. The bar renders every control at all times and
   *  these decide only whether each is clickable, so its shape never changes as
   *  the selection moves -- see the template's own note on why they are not
   *  hidden instead. The conditions are the ones the per-row column used as
   *  `*ngIf`s before the actions moved out of the table. */
  get canOpenSelected(): boolean {
    return this.selectedRecent?.status === 'claimed' && !this.selectionBusy;
  }

  get canActOnSelected(): boolean {
    return this.selectedRecent?.bucket === 'missed' && !this.selectionBusy;
  }

  get canIgnoreSelected(): boolean {
    return this.canActOnSelected && !!this.selectedIgnoreReason;
  }

  /**
   * Opens `app-create-new-document` (AI Assist tab) inline, right here on
   * the dashboard -- NOT via `routerLink="/agent-intake"`, which would
   * navigate away to `DocumentsComponent`'s route just to host the same
   * dialog (that route only exists for the Teams Claim deep link, where
   * there's no existing in-app page to stay on). The dashboard is already
   * inside this same Angular app, so it hosts its own dialog instance
   * instead, same as `DocumentsComponent` does for its own "New" button.
   */
  agentIntakeRequestId: number | null = null;
  displayAgentIntake = false;

  private filtersSubj$ = new Subject<{ from: Date; to: Date; granularity: AgentDashboardGranularity }>();
  private filtersQuery: Subscription = null;
  private recentQuery: Subscription = null;

  trendChartOptions = {
    plugins: { legend: { display: true } },
    scales: { y: { beginAtZero: true } },
  };

  constructor(
    private dashboardApi: AgentDashboardApiService,
    private messageService: MessageService,
  ) {
  }

  ngOnInit(): void {
    const today = new Date();
    const diffToMonday = (today.getDay() + 6) % 7;
    this.dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - diffToMonday);
    this.dateTo = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    this.filtersQuery =
      this.filtersSubj$.pipe(
        debounceTime(300),
        switchMap(f => forkJoin({
          summary: this.dashboardApi.getSummary(toDateParam(f.from), toDateParam(f.to)),
          trend: this.dashboardApi.getTrend(toDateParam(f.from), toDateParam(f.to), f.granularity),
          sla: this.dashboardApi.getSla(toDateParam(f.from), toDateParam(f.to)),
        })),
      ).subscribe({
        next: ({ summary, trend, sla }) => {
          this.summary = summary;
          this.trend = trend;
          this.sla = sla;
          this.buildTrendChart();
          this.loaded = true;
        },
        error: err => {
          console.error('*** AgentDashboardComponent filters: Error: ' + err);
          this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося завантажити дашборд', detail: err?.message || 'Unknown error' });
        }
      });

    this.loadRecent();
    this.reload();
  }

  ngOnDestroy(): void {
    if (this.filtersQuery) { this.filtersQuery.unsubscribe(); }
    if (this.recentQuery) { this.recentQuery.unsubscribe(); }
  }

  /** Pushes the current date range + granularity to trigger a combined reload. */
  reload(): void {
    this.filtersSubj$.next({ from: this.dateFrom, to: this.dateTo, granularity: this.selectedGranularity });
  }

  onGranularityChange(): void {
    this.reload();
  }

  loadRecent(): void {
    this.recentLoaded = false;
    this.recentQuery =
      this.dashboardApi.getRecent(this.recentLimit).subscribe({
        next: items => {
          this.recent = items;
          this.recentLoaded = true;
        },
        error: err => {
          console.error('*** AgentDashboardComponent loadRecent: Error: ' + err);
          this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося завантажити останні звернення', detail: err?.message || 'Unknown error' });
        }
      });
  }

  /** Dashboard equivalent of the Teams Claim button, for a `missed` row.
   *  Reloads both the recent list and the summary cards on success -- a
   *  claimed row leaves the `missed` bucket, which the summary counts too. */
  onClaim(item: AgentDashboardRecentItem): void {
    this.actionInProgress[item.id] = true;
    this.dashboardApi.claimRequest(item.id).subscribe({
      next: () => {
        this.actionInProgress[item.id] = false;
        this.selectedRecent = null;
        this.messageService.add({ severity: 'success', summary: `Звернення #${item.id} взято в роботу` });
        this.loadRecent();
        this.reload();
      },
      error: err => {
        this.actionInProgress[item.id] = false;
        console.error('*** AgentDashboardComponent onClaim: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося взяти в роботу', detail: err?.error?.message || err?.message || 'Unknown error' });
      }
    });
  }

  /** Same gate as the Teams card: `selectedIgnoreReason` must be
   *  set before this can be called -- the template disables the Ignore button until then. */
  onIgnore(item: AgentDashboardRecentItem): void {
    const reason = this.selectedIgnoreReason;
    if (!reason) { return; }

    this.actionInProgress[item.id] = true;
    this.dashboardApi.ignoreRequest(item.id, reason).subscribe({
      next: () => {
        this.actionInProgress[item.id] = false;
        this.selectedIgnoreReason = null;
        this.selectedRecent = null;
        this.messageService.add({ severity: 'success', summary: `Звернення #${item.id} відхилено` });
        this.loadRecent();
        this.reload();
      },
      error: err => {
        this.actionInProgress[item.id] = false;
        console.error('*** AgentDashboardComponent onIgnore: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося відхилити', detail: err?.error?.message || err?.message || 'Unknown error' });
      }
    });
  }

  /** Deliberately narrow (owner-confirmed 2026-08-03): only flips
   *  AgentRequests.status -- see AgentDashboard.ts's own header comment for
   *  why this doesn't write AgentSpamQueue/AgentSpamSenders. */
  onSpam(item: AgentDashboardRecentItem): void {
    this.actionInProgress[item.id] = true;
    this.dashboardApi.spamRequest(item.id).subscribe({
      next: () => {
        this.actionInProgress[item.id] = false;
        this.selectedRecent = null;
        this.messageService.add({ severity: 'success', summary: `Звернення #${item.id} позначено як спам` });
        this.loadRecent();
        this.reload();
      },
      error: err => {
        this.actionInProgress[item.id] = false;
        console.error('*** AgentDashboardComponent onSpam: Error: ' + err);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Не вдалося позначити як спам', detail: err?.error?.message || err?.message || 'Unknown error' });
      }
    });
  }

  /** "Відкрити" on a `claimed` row -- opens the dialog in place instead of
   *  navigating to `/agent-intake`. */
  openAgentIntake(item: AgentDashboardRecentItem): void {
    this.agentIntakeRequestId = item.id;
    this.displayAgentIntake = true;
    this.selectedRecent = null;
  }

  /** `app-create-new-document`'s `(displayChange)` -- clears the request id
   *  on close so a later open doesn't reopen on a stale AI Assist tab, same
   *  reasoning as `DocumentsComponent.onDisplayNDChange()`. */
  onAgentIntakeDisplayChange(v: boolean): void {
    this.displayAgentIntake = v;
    if (!v) { this.agentIntakeRequestId = null; }
  }

  /** `app-create-new-document`'s `(done)` -- fires whether a document was
   *  actually created or the dialog was just closed; either way the
   *  underlying request's status may have changed (resolved/rejected), so
   *  refresh both the recent list and the summary cards. */
  onAgentIntakeDone(): void {
    this.loadRecent();
    this.reload();
  }

  /** Renders minutes as `Xг Yхв` (or `Yхв` under an hour), `—` for null. */
  formatMinutes(m: number | null): string {
    if (m === null || m === undefined) { return '—'; }
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    return hours > 0 ? `${hours}г ${mins}хв` : `${mins}хв`;
  }

  private buildTrendChart(): void {
    this.trendChartData = {
      labels: this.trend.map(t => t.bucket),
      datasets: [
        {
          label: 'Всього',
          data: this.trend.map(t => t.total - t.spam),
          fill: false,
          backgroundColor: '#277622',
          type: 'bar',
        },
        {
          label: 'Спам',
          data: this.trend.map(t => t.spam),
          fill: false,
          backgroundColor: '#BB1010',
          type: 'bar',
        },
      ],
    };
  }
}
